import { useState, useEffect, useRef } from 'react'
import { obtenerUsuarios, verificarPin } from '../db/usuarios'
import { useAuth } from '../context/AuthContext'
import { useConfig } from '../context/ConfigContext'

const MAX_INTENTOS = 3
const BLOQUEO_MS = 30000

export default function Login() {
  const { login } = useAuth()
  const { config } = useConfig()
  const [usuarios, setUsuarios] = useState([])
  const [seleccionado, setSeleccionado] = useState(null)
  const [pin, setPin] = useState('')
  const [error, setError] = useState(false)
  const [intentosFallidos, setIntentosFallidos] = useState(0)
  const [bloqueadoHasta, setBloqueadoHasta] = useState(null)
  const [segundosRestantes, setSegundosRestantes] = useState(0)
  const bloqueadoTimer = useRef(null)

  useEffect(() => {
    obtenerUsuarios().then(u => setUsuarios(u.filter(u => u.activo !== false)))
  }, [])

  // Countdown del bloqueo
  useEffect(() => {
    if (!bloqueadoHasta) return
    const interval = setInterval(() => {
      const restantes = Math.ceil((bloqueadoHasta - Date.now()) / 1000)
      if (restantes <= 0) {
        setBloqueadoHasta(null)
        setSegundosRestantes(0)
        setIntentosFallidos(0)
      } else {
        setSegundosRestantes(restantes)
      }
    }, 200)
    return () => clearInterval(interval)
  }, [bloqueadoHasta])

  function seleccionar(usuario) {
    setSeleccionado(usuario)
    setPin('')
    setError(false)
    setIntentosFallidos(0)
    setBloqueadoHasta(null)
    setSegundosRestantes(0)
    clearTimeout(bloqueadoTimer.current)
  }

  async function presionarTecla(valor) {
    if (bloqueadoHasta && Date.now() < bloqueadoHasta) return
    if (pin.length >= 4) return
    const nuevo = pin + valor
    setPin(nuevo)
    setError(false)

    if (nuevo.length === 4) {
      const usuario = await verificarPin(seleccionado._id, nuevo)
      if (usuario) {
        login(usuario)
      } else {
        const nuevosFallidos = intentosFallidos + 1
        setIntentosFallidos(nuevosFallidos)
        setError(true)

        if (nuevosFallidos >= MAX_INTENTOS) {
          const hasta = Date.now() + BLOQUEO_MS
          setBloqueadoHasta(hasta)
          setSegundosRestantes(Math.ceil(BLOQUEO_MS / 1000))
          setPin('')
          setError(false)
        } else {
          setTimeout(() => { setPin(''); setError(false) }, 700)
        }
      }
    }
  }

  function borrar() {
    if (bloqueadoHasta && Date.now() < bloqueadoHasta) return
    setPin(p => p.slice(0, -1))
    setError(false)
  }

  const bloqueado = bloqueadoHasta && Date.now() < bloqueadoHasta
  const TECLAS = ['1','2','3','4','5','6','7','8','9','','0','⌫']

  return (
    <div className="min-h-screen bg-tienda-bg flex flex-col max-w-sm mx-auto">
      {/* Header */}
      <div className="text-center pt-12 pb-6 px-6">
        <p className="text-4xl mb-2">🛒</p>
        <h1 className="text-2xl font-bold text-tienda-text">{config.nombreTienda}</h1>
      </div>

      {!seleccionado ? (
        /* Selección de usuario */
        <div className="flex-1 px-6">
          <p className="text-tienda-muted text-center text-sm mb-6">Selecciona tu perfil</p>
          <div className="space-y-3">
            {usuarios.map(u => (
              <button
                key={u._id}
                onClick={() => seleccionar(u)}
                className="w-full card p-4 flex items-center gap-4 active:scale-95 transition-transform"
              >
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-xl font-bold text-gray-900 flex-shrink-0
                  ${u.rol === 'admin' ? 'bg-tienda-accent' : 'bg-tienda-success'}`}>
                  {u.nombre[0].toUpperCase()}
                </div>
                <div className="text-left">
                  <p className="font-bold text-tienda-text">{u.nombre}</p>
                  <p className="text-xs text-tienda-muted capitalize">{u.rol === 'admin' ? 'Administrador' : 'Ventas'}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      ) : (
        /* PIN pad */
        <div className="flex-1 flex flex-col px-6">
          <button
            onClick={() => setSeleccionado(null)}
            className="flex items-center gap-2 text-tienda-muted text-sm mb-6"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M15 18l-6-6 6-6" />
            </svg>
            {seleccionado.nombre}
          </button>

          {bloqueado ? (
            /* Estado bloqueado */
            <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center">
              <div className="w-16 h-16 rounded-full bg-tienda-danger/10 flex items-center justify-center">
                <svg className="w-8 h-8 text-tienda-danger" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </div>
              <div>
                <p className="text-tienda-danger font-bold text-lg">Demasiados intentos</p>
                <p className="text-tienda-muted text-sm mt-1">
                  Intenta de nuevo en <span className="font-bold text-tienda-text">{segundosRestantes}s</span>
                </p>
              </div>
            </div>
          ) : (
            <>
              <p className="text-center text-tienda-muted text-sm mb-4">Ingresa tu PIN</p>

              {intentosFallidos > 0 && (
                <p className="text-center text-tienda-danger text-xs mb-3">
                  {MAX_INTENTOS - intentosFallidos} intento{MAX_INTENTOS - intentosFallidos !== 1 ? 's' : ''} restante{MAX_INTENTOS - intentosFallidos !== 1 ? 's' : ''}
                </p>
              )}

              {/* Dots */}
              <div className={`flex justify-center gap-4 mb-8 transition-all ${error ? 'translate-x-2' : ''}`}>
                {[0,1,2,3].map(i => (
                  <div
                    key={i}
                    className={`w-4 h-4 rounded-full border-2 transition-all
                      ${error ? 'border-tienda-danger bg-tienda-danger' :
                        i < pin.length ? 'border-tienda-accent bg-tienda-accent' : 'border-tienda-border'}`}
                  />
                ))}
              </div>

              {/* Numpad */}
              <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto w-full">
                {TECLAS.map((t, i) => {
                  if (t === '') return <div key={i} />
                  return (
                    <button
                      key={i}
                      onClick={() => t === '⌫' ? borrar() : presionarTecla(t)}
                      className={`h-16 rounded-2xl text-xl font-semibold active:scale-90 transition-transform
                        ${t === '⌫'
                          ? 'bg-tienda-surface text-tienda-muted border border-tienda-border'
                          : 'bg-tienda-surface text-tienda-text border border-tienda-border'}`}
                    >
                      {t}
                    </button>
                  )
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
